import { Controller } from "@hotwired/stimulus"
import * as bootstrap from "bootstrap"
import { logger } from "../logger"

// Table Dropdown Controller
// Moves dropdown menus to body (portal pattern) to escape overflow:auto scroll containers
// This is the most reliable way to ensure dropdowns are not clipped by parent containers
//
// Usage:
//   span.dropdown[data-controller="table-dropdown"]
//     a[href="#" data-bs-toggle="dropdown"]
//     .dropdown-menu
//
export default class extends Controller {
  connect() {
    // Find the dropdown toggle element
    this.toggle = this.element.querySelector('[data-bs-toggle="dropdown"]')
    if (!this.toggle) {
      logger.warn('table-dropdown: No dropdown toggle found')
      return
    }

    // Find the dropdown menu element
    this.menu = this.element.querySelector('.dropdown-menu')
    if (!this.menu) {
      logger.warn('table-dropdown: No dropdown menu found')
      return
    }

    // Store original parent for cleanup
    this.originalParent = this.menu.parentElement

    // Get footer height to account for fixed footer when calculating dropdown position
    const footerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--data-table-footer-height') || '60', 10)

    // Move menu to body when dropdown is about to show
    this.element.addEventListener('show.bs.dropdown', this.handleShow)
    this.element.addEventListener('hidden.bs.dropdown', this.handleHidden)

    // Initialize Bootstrap dropdown with fixed positioning strategy
    this.dropdown = new bootstrap.Dropdown(this.toggle, {
      popperConfig: {
        strategy: 'fixed',
        placement: 'bottom-end',
        modifiers: [
          {
            name: 'flip',
            options: {
              fallbackPlacements: ['top-end', 'bottom-start', 'top-start'],
              boundary: 'viewport',
              padding: { bottom: footerHeight + 16 }
            }
          },
          {
            name: 'preventOverflow',
            options: {
              boundary: 'viewport',
              padding: { top: 8, right: 8, bottom: footerHeight + 16, left: 8 }
            }
          },
          {
            name: 'offset',
            options: {
              offset: [0, 4]
            }
          }
        ]
      }
    })

    logger.log('table-dropdown: Initialized with portal pattern')
  }

  // Arrow function to preserve 'this' context
  handleShow = () => {
    // Move menu to body to escape overflow containers
    document.body.appendChild(this.menu)
    logger.log('table-dropdown: Menu moved to body')
  }

  handleHidden = () => {
    // Move menu back to original location for proper DOM cleanup
    if (this.originalParent && this.menu.parentElement === document.body) {
      this.originalParent.appendChild(this.menu)
      logger.log('table-dropdown: Menu returned to original parent')
    }
  }

  disconnect() {
    // Remove event listeners
    this.element.removeEventListener('show.bs.dropdown', this.handleShow)
    this.element.removeEventListener('hidden.bs.dropdown', this.handleHidden)

    // Ensure menu is back in original location
    if (this.menu && this.originalParent && this.menu.parentElement === document.body) {
      this.originalParent.appendChild(this.menu)
    }

    if (this.dropdown) {
      this.dropdown.dispose()
      this.dropdown = null
    }
  }
}
